var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BargeInShadowDetector, } from './barge-in-detector';
import { DEFAULT_BARGE_IN_CONFIG, normalizeBargeInConfig, toDetectorParams, } from './barge-in-config';
import { resampleMonoFloat32 } from './realtime/audio-utils';
import { getSileroRuntime } from './silero-loader';
import { SAMPLE_RATE, SileroVadStream, WINDOW_MS, } from './silero-vad-stream';
/**
 * VA-27852 Phase 5 — Silero shadow controller.
 *
 * The seam between the existing microphone handler and the proven Silero stack.
 * It owns the lazy loader handle, the stream, and a probability-driven instance
 * of the existing barge-in state machine; the interview context calls five short
 * methods and holds no Silero state of its own.
 *
 * SHADOW ONLY, AND STRUCTURALLY SO. This module cannot interrupt anything. It
 * holds no reference to the websocket, to playback, to `stopAssistantPlaybackNow`
 * or to any backend message — the same property that made B2.1 safe to default
 * on, asserted here by test rather than asserted by intent. What it produces is a
 * transition event and counters.
 *
 * WHY IT LIVES OUTSIDE THE CONTEXT. `interview-meeting-context.tsx` cannot be
 * exercised without mounting the whole interview, and per-frame audio logic is
 * exactly the kind that needs frame-by-frame tests. Keeping the controller pure
 * — no React, no hooks, no DOM beyond what the loader itself does — means the
 * integration can be driven directly, the same reason `barge-in-detector.ts` and
 * `silero-vad-stream.ts` are separate.
 */
/**
 * Detector parameters for PROBABILITY input.
 *
 * `BargeInShadowDetector`'s state machine is unit-agnostic — it accumulates
 * evidence while `value >= params.rmsFloor` and abandons below it. Only the
 * parameter NAME encodes RMS. Reusing the class unchanged keeps every existing
 * detector test valid and keeps this phase to one behaviour change; renaming the
 * field belongs to the phase that deletes the RMS path.
 *
 * WHERE THE NUMBERS COME FROM:
 *
 * - 0.5 is `VAD_SPEECH_PROB_THRESHOLD`, inherited unchanged from the backend.
 * - 288 ms is 9 windows of 32 ms. The backend's real onset bar is ~300 ms
 *   (ONSET_MIN_FRAMES = 3 counts 100 ms WEBSOCKET CHUNKS, not 32 ms windows), and
 *   288 is the window-quantised value nearest it. Confirmation can only land on a
 *   window boundary, so stating 288 makes the quantisation visible instead of
 *   letting 300 silently become 320.
 * - 64 ms is 2 windows, mirroring the backend's SPEECH_RESET_MIN_FRAMES.
 * - 896 ms is NOT arbitrary and NOT decorative. `maxOnsetWindowMs` is the only
 *   transition from `onset` back to `armed` under alternating evidence, because
 *   `belowMs` resets to zero on every above-threshold frame so `abandonMs` can
 *   never fire there. It is coupled to `onsetMs`: recovering from a single dip
 *   mid-onset needs ~288 + 32 + 288 = 608 ms, and two dips ~864 ms. Left at the
 *   RMS default of 500 it would abandon before evidence could ever accumulate,
 *   and real speech would become undetectable.
 *
 * All of these REQUIRE ACOUSTIC CALIBRATION against a live interview. They are a
 * reasoned starting point inherited from a detector proven in production on the
 * same model — not a tuned result.
 */
export const SILERO_SHADOW_PARAMS = {
    rmsFloor: 0.5,
    onsetMs: 288,
    abandonMs: 64,
    maxOnsetWindowMs: 896,
};
/** Gap beyond which the model's state no longer describes contiguous audio. */
const FRAME_GAP_RESET_MS = 250;
const CADENCE_SAMPLE_CAP = 512;
export class SileroShadowController {
    constructor(options = {}) {
        var _a;
        this.statusValue = 'idle';
        this.stream = null;
        this.runtime = null;
        this.startPromise = null;
        /** Whether the assistant was producing output on the most recent frame. */
        this.armed = false;
        this.lastFrameAt = 0;
        /** Caller-supplied identity of the assistant turn the last frame belonged to. */
        this.turnAtLastPush = 0;
        this.framesObserved = 0;
        this.windowsObserved = 0;
        this.notices = 0;
        this.events = {};
        this.peakProbability = 0;
        this.peakLevelDbfs = -100;
        this.belowFloorWindows = 0;
        this.config = Object.assign({}, DEFAULT_BARGE_IN_CONFIG);
        this.cadence = [];
        this.errorMessage = null;
        this.options = options;
        this.detector = new BargeInShadowDetector((_a = options.params) !== null && _a !== void 0 ? _a : SILERO_SHADOW_PARAMS);
    }
    /**
     * VA-28665: apply admin-tuned thresholds (from `session_create`). Anything unreadable
     * keeps today's value — see `normalizeBargeInConfig`.
     */
    setConfig(raw) {
        this.config = normalizeBargeInConfig(raw);
        this.detector.setParams(toDetectorParams(this.config));
        this.log('[azure][silero] barge-in thresholds applied', this.config);
        return Object.assign({}, this.config);
    }
    get status() {
        return this.statusValue;
    }
    snapshot() {
        var _a;
        const sorted = [...this.cadence].sort((a, b) => a - b);
        const at = (q) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))] : 0);
        return {
            status: this.statusValue,
            framesObserved: this.framesObserved,
            windowsObserved: this.windowsObserved,
            notices: this.notices,
            events: Object.assign({}, this.events),
            peakProbability: this.peakProbability,
            peakLevelDbfs: Number(this.peakLevelDbfs.toFixed(1)),
            belowFloorWindows: this.belowFloorWindows,
            config: Object.assign({}, this.config),
            cadenceMs: {
                count: sorted.length,
                p50: Number(at(0.5).toFixed(3)),
                p95: Number(at(0.95).toFixed(3)),
                max: Number(((_a = sorted[sorted.length - 1]) !== null && _a !== void 0 ? _a : 0).toFixed(3)),
            },
            error: this.errorMessage,
        };
    }
    /**
     * Begin loading, at most once.
     *
     * Deliberately fire-and-forget: the microphone handler must never await ORT.
     * Called when the microphone path starts rather than on mount, so the runtime
     * and model are not fetched on the join or permissions screens — and never at
     * all in an interview that does not reach audio.
     *
     * Idempotent: safe on every device re-acquire. The loader is single-flight and
     * ref-counted underneath, so repeat calls cannot create a second session.
     */
    start() {
        if (this.statusValue !== 'idle' || this.startPromise)
            return;
        this.statusValue = 'loading';
        this.startPromise = this.initialise();
        // Attached so a rejection can never surface as an unhandled rejection; the
        // failure is already recorded on the instance by `initialise`.
        void this.startPromise.catch(() => undefined);
    }
    initialise() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const getRuntime = (_a = this.options.getRuntime) !== null && _a !== void 0 ? _a : getSileroRuntime;
                const runtime = yield getRuntime();
                // Disposed while the loader was still working. Hand the handle straight
                // back rather than leaking a session for a controller nobody holds.
                if (this.statusValue === 'disposed') {
                    yield runtime.release().catch(() => undefined);
                    return;
                }
                this.runtime = runtime;
                this.stream = new SileroVadStream(runtime, {
                    onWindow: (window) => this.onWindow(window.probability, window.levelDbfs),
                    onNotice: (notice) => this.onNotice(notice),
                });
                this.statusValue = 'ready';
                this.log('[azure][silero] shadow VAD ready');
            }
            catch (error) {
                // FAIL CLOSED. No RMS substitute, no retry loop here — the loader already
                // bounds its own attempts. Shadow measurement is simply unavailable for
                // this session, and the microphone path is untouched either way.
                this.statusValue = 'unavailable';
                this.errorMessage = error instanceof Error ? error.message : String(error);
                this.log('[azure][silero] shadow VAD unavailable', this.errorMessage);
            }
        });
    }
    /**
     * Feed one microphone frame captured while the assistant is producing output.
     *
     * `sampleRate` is the AudioContext's ACTUAL rate rather than an assumed 48 kHz,
     * and resampling uses the same `resampleMonoFloat32` the STT path uses — not a
     * better one. Matching what the backend model is fed matters more than a
     * cleaner resample, and a different resampler would make the two sides see
     * different signals.
     */
    observeAssistantFrame(samples, sampleRate, turnToken = 0) {
        if (this.statusValue === 'disposed' || !samples || samples.length === 0)
            return;
        this.turnAtLastPush = turnToken;
        const now = this.now();
        if (this.lastFrameAt) {
            const gap = now - this.lastFrameAt;
            if (this.cadence.length < CADENCE_SAMPLE_CAP)
                this.cadence.push(gap);
            // A long gap means capture was suspended (hidden tab, device switch, audio
            // interruption). The LSTM state describes audio that is no longer adjacent
            // to what comes next, so start again rather than bridge the hole.
            if (this.armed && gap > FRAME_GAP_RESET_MS)
                this.reset();
        }
        this.lastFrameAt = now;
        this.armed = true;
        this.framesObserved += 1;
        const stream = this.stream;
        if (!stream || this.statusValue !== 'ready')
            return;
        stream.push(sampleRate === SAMPLE_RATE ? samples : resampleMonoFloat32(samples, sampleRate, SAMPLE_RATE));
    }
    /**
     * The assistant is not producing output. Drops in-flight evidence so it can
     * never carry across the boundary into a listening turn — the same contract the
     * RMS shadow detector already has.
     */
    observeIdle() {
        var _a;
        if (this.statusValue === 'disposed')
            return;
        if (!this.armed)
            return;
        this.armed = false;
        this.lastFrameAt = 0;
        this.detector.observe(0, 0, false);
        (_a = this.stream) === null || _a === void 0 ? void 0 : _a.reset();
    }
    /** Drop model state, context, remainder and detector progress together. */
    reset() {
        var _a;
        if (this.statusValue === 'disposed')
            return;
        this.detector.reset();
        (_a = this.stream) === null || _a === void 0 ? void 0 : _a.reset();
        this.lastFrameAt = 0;
    }
    /** Permanent teardown. Safe while initialisation is still pending. */
    dispose() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.statusValue === 'disposed')
                return;
            const stream = this.stream;
            const runtime = this.runtime;
            this.statusValue = 'disposed';
            this.stream = null;
            this.runtime = null;
            this.armed = false;
            if (stream) {
                // Releases the stream and, through it, this controller's loader handle.
                yield stream.release().catch(() => undefined);
            }
            else if (runtime) {
                yield runtime.release().catch(() => undefined);
            }
        });
    }
    onWindow(probability, levelDbfs = 0) {
        var _a, _b, _c, _d, _e;
        if (this.statusValue !== 'ready')
            return;
        this.windowsObserved += 1;
        if (this.armed && probability > this.peakProbability)
            this.peakProbability = probability;
        if (this.armed && levelDbfs > this.peakLevelDbfs)
            this.peakLevelDbfs = levelDbfs;
        // VA-28665: the loudness floor. A window quieter than the floor is not speech for
        // barge-in, however voice-like — that is a voice across the room, not the candidate.
        let evidence = probability;
        if (levelDbfs < this.config.min_level_dbfs) {
            if (probability >= this.config.probability)
                this.belowFloorWindows += 1;
            evidence = 0;
        }
        try {
            (_b = (_a = this.options).onWindow) === null || _b === void 0 ? void 0 : _b.call(_a, probability, levelDbfs);
        }
        catch (_f) {
            // A readout listener must never break detection.
        }
        // `armed` is read at COMPLETION, not at push. Inference is asynchronous, so a
        // window can resolve after the assistant has stopped; observing it unarmed
        // makes the detector fall back to idle, which is the safe direction. In
        // shadow mode a late result has no side effect to guard beyond that, so no
        // turn token is introduced here — see the Phase 5 report.
        const event = this.detector.observe(evidence, WINDOW_MS, this.armed);
        if (!event)
            return;
        this.events[event] = ((_c = this.events[event]) !== null && _c !== void 0 ? _c : 0) + 1;
        const snapshot = this.snapshot();
        this.log(`[azure][silero][shadow] ${event}`, {
            probability: Number(probability.toFixed(4)),
            levelDbfs: Number(levelDbfs.toFixed(1)),
            peak: Number(snapshot.peakProbability.toFixed(4)),
            peakLevelDbfs: snapshot.peakLevelDbfs,
            threshold: this.config.probability,
            onsetMs: this.config.onset_ms,
            minLevelDbfs: this.config.min_level_dbfs,
            windows: snapshot.windowsObserved,
            detector: this.detector.snapshot().counters,
        });
        (_e = (_d = this.options).onEvent) === null || _e === void 0 ? void 0 : _e.call(_d, event, snapshot, this.turnAtLastPush);
    }
    onNotice(notice) {
        var _a, _b;
        this.notices += 1;
        this.log(`[azure][silero][shadow] notice ${notice.type}`, notice);
        (_b = (_a = this.options).onNotice) === null || _b === void 0 ? void 0 : _b.call(_a, notice);
    }
    log(message, detail) {
        if (this.options.log) {
            this.options.log(message, detail);
            return;
        }
        // eslint-disable-next-line no-console
        if (detail === undefined)
            console.info(message);
        // eslint-disable-next-line no-console
        else
            console.info(message, detail);
    }
    now() {
        return typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
    }
}
